import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Order() {
  const { tableId } = useParams();
  const navigate    = useNavigate();

  const [menu, setMenu]             = useState([]);
  const [activecat, setActivecat]   = useState(null);
  const [catTabsPerRow, setCatTabsPerRow] = useState(6);
  const catTabsRef = useRef(null);
  const [catTabHeight, setCatTabHeight] = useState(null);
  const cartKey = `cafepos_cart_${tableId}`;
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(cartKey) || '[]'); } catch { return []; }
  });
  const [table, setTable]           = useState(null);
  const [orderNote, setOrderNote]   = useState('');
  const [sending, setSending]       = useState(false);
  const [toast, setToast]           = useState('');
  const [openOrders, setOpenOrders] = useState([]);
  const [itemModal, setItemModal]   = useState(null);
  const [payModal, setPayModal]     = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [payTypeModal, setPayTypeModal] = useState(null); // { id: 'new'|orderId, total, order? }
  const [nipReceiptModal, setNipReceiptModal] = useState(null); // { id, total, isNew }
  const [contractor, setContractor] = useState(null);
  const [contractorModal, setContractorModal] = useState(false);
  const [contractorDiscounts, setContractorDiscounts] = useState([]);
  const [addToOrderId, setAddToOrderId] = useState(null); // ID zamówienia, do którego dopisujemy pozycje (lub null = nowe zamówienie)

  const showToast = useCallback((msg) => {
    setToast(msg); setTimeout(() => setToast(''), 2500);
  }, []);

  // Pobierz zniżki kontrahenta gdy wybrany
  useEffect(() => {
    const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
    fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(s => { const n = parseInt(s.category_tabs_per_row, 10); if (n > 0) setCatTabsPerRow(n); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    function measure() {
      const el = catTabsRef.current;
      if (!el) return;
      const buttons = el.querySelectorAll('.cat-tab');
      if (!buttons.length) return;
      // Zresetuj wysokość, żeby zmierzyć naturalną (może się zmienić po zmianie szerokości okna)
      buttons.forEach(b => { b.style.height = 'auto'; });
      const max = Math.max(...Array.from(buttons).map(b => b.offsetHeight));
      setCatTabHeight(max);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [menu, catTabsPerRow]);

  useEffect(() => {
    if (!contractor) {
      setContractorDiscounts([]);
      // Usuń zniżki z koszyka — przywróć oryginalne ceny
      setCart(prev => prev.map(c => ({ ...c, price: c.base_price ?? c.price })));
      return;
    }
    const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
    fetch(`/api/contractors/${contractor.id}/discounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(discounts => {
        setContractorDiscounts(discounts);
        // Przelicz ceny w koszyku ze zniżkami kontrahenta - WARIANT i KAŻDY MODYFIKATOR
        // liczone osobno (tak samo jak w modalu przy dodawaniu), a nie jako jeden zlepek ceny
        setCart(prev => prev.map(c => {
          const modifiersRaw = c.modifiers || [];
          const modRawTotal = modifiersRaw.reduce((s, m) => s + Number(m.price), 0);
          const variantBasePrice = (c.base_price ?? c.price) - modRawTotal;

          // Zniżka na produkt/wariant
          const vd = discounts.find(x =>
            x.menu_item_id === c.menu_item_id &&
            (c.variant_option_id ? x.variant_option_id == c.variant_option_id : !x.variant_option_id)
          );
          let variantPrice = variantBasePrice;
          if (vd) {
            if (vd.fixed_price !== null && vd.fixed_price !== undefined) variantPrice = Number(vd.fixed_price);
            else if (vd.discount_percent > 0) variantPrice = Math.round(variantBasePrice * (1 - vd.discount_percent / 100) * 100) / 100;
          }

          // Zniżka na każdy modyfikator z osobna (modyfikatory to własne pozycje menu, własne ID)
          const modDiscountedTotal = modifiersRaw.reduce((sum, m) => {
            const md = discounts.find(x => x.menu_item_id === m.id && !x.variant_option_id);
            let p = Number(m.price);
            if (md) {
              if (md.fixed_price !== null && md.fixed_price !== undefined) p = Number(md.fixed_price);
              else if (md.discount_percent > 0) p = Math.round(Number(m.price) * (1 - md.discount_percent / 100) * 100) / 100;
            }
            return sum + p;
          }, 0);

          return { ...c, base_price: variantBasePrice + modRawTotal, price: variantPrice + modDiscountedTotal };
        }));
      })
      .catch(() => {});
  }, [contractor]);

  const getDiscountedPrice = (item, variantOptionId = null) => {
    if (!contractorDiscounts.length) return null;
    // Szukaj zniżki dla konkretnego wariantu lub dla produktu bez wariantu
    const d = contractorDiscounts.find(x =>
      x.menu_item_id === item.id &&
      (variantOptionId ? x.variant_option_id == variantOptionId : !x.variant_option_id)
    );
    if (!d) return null;
    const basePrice = variantOptionId
      ? Number(item.variant_groups?.flatMap(g => g.options).find(o => o.id === variantOptionId)?.price || item.price)
      : Number(item.price);
    if (d.fixed_price !== null && d.fixed_price !== undefined) return Number(d.fixed_price);
    if (d.discount_percent > 0) return Math.round(basePrice * (1 - d.discount_percent / 100) * 100) / 100;
    return null;
  };

  const loadOrders = useCallback(async () => {
    const allOrders = await api.getOrders({ table_id: tableId });
    const orders = allOrders.filter(o => o.status === 'active');
    setOpenOrders(orders);
  }, [tableId]);

  useEffect(() => {
    async function load() {
      const [menuData, tablesData] = await Promise.all([api.getMenu(), api.getTables()]);
      setMenu(menuData);
      if (menuData.length) setActivecat(menuData[0].id);
      setTable(tablesData.find(t => t.id === parseInt(tableId)));
    }
    load();
    loadOrders();
    const poll = setInterval(loadOrders, 5000);
    return () => clearInterval(poll);
  }, [tableId, loadOrders]);

  // Synchronizuj koszyk z sessionStorage
  React.useEffect(() => {
    try {
      if (cart.length) sessionStorage.setItem(cartKey, JSON.stringify(cart));
      else sessionStorage.removeItem(cartKey);
    } catch {}
  }, [cart, cartKey]);

  function handleItemClick(item) {
    const hasVariants  = item.variant_groups && item.variant_groups.length > 0;
    const hasModifiers = item.modifier_groups && item.modifier_groups.length > 0;
    if (hasVariants || hasModifiers) {
      setItemModal(item);
    } else {
      addToCart(item, null, null, []);
    }
  }

  function addToCart(item, variantOptionId, variantLabel, selectedMods, itemNote) {
    const modKey = selectedMods.map(m => m.id).sort().join('-');
    const key    = `${item.id}_${variantOptionId || 'base'}_${modKey}_${itemNote || ''}`;
    const variantPrice = variantOptionId
      ? Number(item.variant_groups.flatMap(g => g.options).find(o => o.id === variantOptionId)?.price || item.price)
      : Number(item.price);
    const discountedPrice = getDiscountedPrice(item, variantOptionId) ?? variantPrice;
    // selectedMods trzyma SUROWE ceny (bez zniżki) - zniżkę liczymy tutaj osobno na każdym modyfikatorze,
    // tak samo jak dla wariantu, żeby dało się to poprawnie przeliczyć później przy zmianie kontrahenta
    const modRawTotal = selectedMods.reduce((s, m) => s + Number(m.price), 0);
    const modDiscountedTotal = selectedMods.reduce((s, m) =>
      s + (getDiscountedPrice({ id: m.id, price: m.price }, null) ?? Number(m.price)), 0);
    const totalPrice = discountedPrice + modDiscountedTotal;

    setCart(prev => {
      const existing = prev.find(c => c.key === key);
      if (existing) return prev.map(c => c.key === key ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, {
        key, menu_item_id: item.id, name: item.name,
        price: totalPrice, base_price: variantPrice + modRawTotal, qty: 1,
        variant_option_id: variantOptionId,
        variant_label: variantLabel,
        modifier_ids: selectedMods.map(m => m.id),
        modifier_labels: selectedMods.map(m => m.name),
        modifiers: selectedMods.map(m => ({ id: m.id, name: m.name, price: Number(m.price) })), // surowe ceny - do przeliczeń
        notes: itemNote || null,
      }];
    });
    const modStr = selectedMods.length ? ` + ${selectedMods.map(m => m.name).join(', ')}` : '';
    showToast(`Dodano: ${item.name}${variantLabel ? ` (${variantLabel})` : ''}${modStr}`);
    setItemModal(null);
  }

  function changeQty(key, delta) {
    setCart(prev => prev.map(c => c.key === key ? { ...c, qty: c.qty + delta } : c).filter(c => c.qty > 0));
  }

  async function printAccountSlip(orderId) {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const useFiscal = await (async () => {
        const res = await fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } });
        const s = await res.json();
        return s.account_slip_use_fiscal === '1';
      })();
      const endpoint = useFiscal ? '/api/fiscal/print-account-slip' : '/api/receipt/print';
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order_id: orderId }),
      });
    } catch {} // Ignoruj błędy drukowania
  }

  async function printPreBill(orderId) {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res0 = await fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } });
      const s = await res0.json();
      const useFiscal = s.pre_bill_use_fiscal === '1';
      const endpoint = useFiscal ? '/api/fiscal/print-pre-bill' : '/api/receipt/print';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order_id: orderId }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Błąd druku');
      showToast('✓ Rachunek wstępny wydrukowany');
    } catch (err) { showToast(`Błąd: ${err.message}`); }
  }

  async function sendToAccount() {
    if (!cart.length || !contractor) return;
    setSending(true);
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          table_id: parseInt(tableId),
          items: cart.map(c => ({
            menu_item_id:      c.menu_item_id,
            qty:               c.qty,
            variant_option_id: c.variant_option_id || undefined,
            modifier_ids:      c.modifier_ids || [],
            notes:             c.notes || undefined,
          })),
          contractor_id: contractor.id,
          notes: orderNote.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Błąd wysyłania zamówienia');
      const order_id = data.order_id;
      if (await shouldPrintReceipt('account')) await printAccountSlip(order_id);
      setCart([]);
      setOrderNote('');
      try { sessionStorage.removeItem(cartKey); } catch {}
      showToast(`✓ Zamówienie na konto: ${contractor.name}`);
      navigate('/');
    } catch (err) { showToast(`Błąd: ${err.message}`); }
    finally { setSending(false); }
  }

  async function sendOrder() {
    if (!cart.length) return;
    setSending(true);
    try {
      if (addToOrderId) {
        // Tryb dopisywania pozycji do istniejącego (już wydanego / w trakcie) zamówienia
        const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
        const res = await fetch(`/api/orders/${addToOrderId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            items: cart.map(c => ({
              menu_item_id:      c.menu_item_id,
              qty:               c.qty,
              variant_option_id: c.variant_option_id || undefined,
              modifier_ids:      c.modifier_ids || [],
              notes:             c.notes || undefined,
            })),
            notes: orderNote.trim() || undefined,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Błąd dopisywania pozycji');
        setAddToOrderId(null);
        setCart([]);
        setOrderNote('');
        try { sessionStorage.removeItem(cartKey); } catch {}
        showToast('✓ Dopisano pozycje do zamówienia');
        navigate('/');
        return;
      }

      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          table_id: parseInt(tableId),
          items: cart.map(c => ({
            menu_item_id:      c.menu_item_id,
            qty:               c.qty,
            variant_option_id: c.variant_option_id || undefined,
            modifier_ids:      c.modifier_ids || [],
            notes:             c.notes || undefined,
          })),
          notes: orderNote.trim() || undefined,
          // "Wyślij do kuchni" nie przypisuje do konta kontrahenta - kontrahent tu służy tylko do zniżek cenowych
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Błąd wysyłania zamówienia');
      const order_id = data.order_id;
      if (await shouldPrintReceipt('send')) await printReceipt(order_id);
      setCart([]);
      setOrderNote('');
      try { sessionStorage.removeItem(cartKey); } catch {}
      navigate('/');
    } catch (err) { showToast(`Błąd: ${err.message}`); }
    finally { setSending(false); }
  }

  async function markSent(orderId) {
    try {
      await api.setDelivered(orderId);
      navigate('/');
    } catch (err) { showToast(`Błąd: ${err.message}`); }
  }

  async function sendAndPay(paymentMethod) {
    if (!cart.length) return;
    setSending(true);
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          table_id: parseInt(tableId),
          items: cart.map(c => ({
            menu_item_id:      c.menu_item_id,
            qty:               c.qty,
            variant_option_id: c.variant_option_id || undefined,
            modifier_ids:      c.modifier_ids || [],
            notes:             c.notes || undefined,
          })),
          payment_method: paymentMethod,
          contractor_id: contractor?.id || undefined,
          notes: orderNote.trim() || undefined,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Błąd wysyłania zamówienia');
      setCart([]);
      setOrderNote('');
      try { sessionStorage.removeItem(cartKey); } catch {}
      setPayModal(null);
      if (await shouldOpenDrawer(paymentMethod)) await openDrawer();
      if (await shouldPrintReceipt('pay')) await printReceipt(result?.order_id);
      if (paymentMethod !== 'receipt' && await shouldPrintFiscal()) await printFiscal(result?.order_id, paymentMethod);
      navigate('/');
    } catch (err) { showToast(`Błąd: ${err.message}`); }
    finally { setSending(false); }
  }

  async function printFiscal(orderId, paymentMethod, buyerNip = null) {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      // Pobierz pozycje zamówienia z VAT z menu_items
      const res = await fetch(`/api/orders/${orderId}/items`, { headers: { Authorization: `Bearer ${token}` } });
      const orderItems = await res.json();
      if (!orderItems?.length) return;
      const items = orderItems.map(i => ({
        name: i.name,
        price: Number(i.price),
        qty: Number(i.qty),
        vat_rate: Number(i.vat_rate ?? 23),
      }));
      const total = items.reduce((s, i) => s + i.price * i.qty, 0);
      await fetch('/api/fiscal/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ items, payment_method: paymentMethod, total, training: false, ...(buyerNip ? { buyer_nip: buyerNip } : {}) }),
      });
    } catch {} // Ignoruj błędy — płatność już zapisana
  }

  async function shouldPrintFiscal() {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res = await fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } });
      const s = await res.json();
      return s['fiscal_on_pay'] === '1';
    } catch { return false; }
  }

  async function printReceipt(orderId) {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      await fetch('/api/receipt/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order_id: orderId }),
      });
    } catch {} // Ignoruj błędy drukowania
  }

  async function shouldPrintReceipt(event) {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res = await fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } });
      const s = await res.json();
      return s[`receipt_on_${event}`] === '1';
    } catch { return false; }
  }

  async function openDrawer() {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      await fetch('/api/drawer/open', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } });
    } catch {} // Ignoruj błędy szuflady — płatność i tak jest zapisana
  }

  async function shouldOpenDrawer(method) {
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res = await fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } });
      const s = await res.json();
      return s[`drawer_on_${method}`] === '1';
    } catch { return false; }
  }

  async function payOrder(orderId, paymentMethod, skipFiscal = false) {
    try {
      await api.setPayment(orderId, paymentMethod);
      if (await shouldOpenDrawer(paymentMethod)) await openDrawer();
      if (await shouldPrintReceipt('pay')) await printReceipt(orderId);
      if (!skipFiscal && paymentMethod !== 'receipt' && await shouldPrintFiscal()) await printFiscal(orderId, paymentMethod);
      showToast('✓ Zamówienie opłacone');
      const allOrders = await api.getOrders({ table_id: tableId });
      const orders = allOrders.filter(o => o.status === 'active');
      setOpenOrders(orders);
      if (orders.length === 0) navigate('/');
    } catch (err) { showToast(`Błąd: ${err.message}`); }
  }

  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const activeItems = menu.find(c => c.id === activecat)?.items || [];

  return (
    <div className="order-page">
      <div className="order-header">
        <button className="back-btn" onClick={() => navigate('/')}>← Stoliki</button>
        {parseInt(tableId) === 41
          ? <h2>⚡ Szybki rachunek</h2>
          : <h2>Stolik {table?.number} · {table?.seats} os.</h2>
        }
        <button
          onClick={() => setContractorModal(true)}
          style={{
            marginLeft: 'auto', padding: '6px 12px', borderRadius: 8, border: '0.5px solid var(--border)',
            background: contractor ? '#eff6ff' : 'var(--bg)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            color: contractor ? 'var(--blue)' : 'var(--text-muted)',
          }}
        >
          {contractor ? `🏢 ${contractor.name}` : '+ Kontrahent'}
        </button>
      </div>
      {contractorModal && (
        <ContractorSelectModal
          onSelect={c => { setContractor(c); setContractorModal(false); }}
          onClear={() => { setContractor(null); setContractorModal(false); }}
          onClose={() => setContractorModal(false)}
          current={contractor}
        />
      )}

      <div className="order-layout">
        <div className="menu-col">
          <div className="cat-tabs" ref={catTabsRef} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, width: '100%' }}>
            {menu.map(cat => {
              const isActive = activecat === cat.id;
              const color = cat.color || '#185FA5';
              const tabBasis = `calc((100% - ${(catTabsPerRow - 1) * 8}px) / ${catTabsPerRow})`;
              const heightStyle = catTabHeight ? { height: catTabHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {};
              return (
                <button key={cat.id}
                  className="cat-tab"
                  onClick={() => setActivecat(cat.id)}
                  style={isActive ? {
                    background: 'var(--surface)',
                    color: color,
                    border: `2px solid ${color}`,
                    fontWeight: 600,
                    flex: `0 0 ${tabBasis}`,
                    borderRadius: 8,
                    textAlign: 'center',
                    ...heightStyle,
                  } : {
                    background: color,
                    color: '#fff',
                    border: 'none',
                    fontWeight: 500,
                    opacity: 0.9,
                    flex: `0 0 ${tabBasis}`,
                    borderRadius: 8,
                    textAlign: 'center',
                    ...heightStyle,
                  }}>
                  {cat.name}
                </button>
              );
            })}
          </div>
          <div className="menu-grid">
            {activeItems.map(item => (
              <div key={item.id} className="menu-item" onClick={() => handleItemClick(item)}>
                <div className="mi-name">{item.name}</div>
                {item.variant_groups?.length > 0
                  ? (() => {
                      const allOptions = item.variant_groups.flatMap(g => g.options);
                      const discountedPrices = allOptions.map(o => getDiscountedPrice(item, o.id) ?? Number(o.price));
                      const minOrig = Math.min(...allOptions.map(o => Number(o.price)));
                      const minDisc = Math.min(...discountedPrices);
                      const hasDiscount = discountedPrices.some((p, i) => p !== Number(allOptions[i].price));
                      return hasDiscount ? (
                        <div className="mi-price">
                          <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: 11, marginRight: 4 }}>od {minOrig.toFixed(2)}</span>
                          <span style={{ color: '#16a34a', fontWeight: 700 }}>od {minDisc.toFixed(2)} zł ▾</span>
                        </div>
                      ) : (
                        <div className="mi-price" style={{ color: 'var(--blue-dark)' }}>od {minOrig.toFixed(2)} zł ▾</div>
                      );
                    })()
                  : (() => {
                      const discounted = getDiscountedPrice(item);
                      return discounted !== null ? (
                        <div className="mi-price">
                          <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: 11, marginRight: 4 }}>{Number(item.price).toFixed(2)}</span>
                          <span style={{ color: '#16a34a', fontWeight: 700 }}>{discounted.toFixed(2)} zł</span>
                        </div>
                      ) : (
                        <div className="mi-price">{Number(item.price).toFixed(2)} zł</div>
                      );
                    })()
                }
                {item.modifier_groups?.length > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>+ modyfikatory</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="cart-col">
          <div className="cart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Nowe zamówienie</span>
            <button className="clear-btn" onClick={() => setCart([])} style={{ color: 'var(--red, #A32D2D)' }}>Wyczyść</button>
          </div>
          <div className="cart-items">
            {cart.length === 0 && <div className="cart-empty">Wybierz pozycje z menu</div>}
            {cart.map(item => (
              <div key={item.key} className="cart-row">
                <div style={{ flex: 1 }}>
                  <div className="cart-name">{item.name}</div>
                  {item.variant_label && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.variant_label}</div>}
                  {item.modifier_labels?.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>+ {item.modifier_labels.join(', ')}</div>
                  )}
                  {item.notes && (
                    <div style={{ fontSize: 11, color: '#8A5A00', fontWeight: 500 }}>⚠ {item.notes}</div>
                  )}
                </div>
                <div className="qty-ctrl">
                  <button onClick={() => changeQty(item.key, -1)}>−</button>
                  <span>{item.qty}</span>
                  <button onClick={() => changeQty(item.key, 1)}>+</button>
                </div>
                <span className="cart-price">{(item.price * item.qty).toFixed(2)} zł</span>
              </div>
            ))}
          </div>
          {cart.length > 0 && (
            <div style={{ padding: '8px 16px 0' }}>
              <input
                type="text"
                value={orderNote}
                onChange={e => setOrderNote(e.target.value)}
                placeholder="Komentarz do zamówienia (opcjonalnie, tylko dla kuchni)"
                maxLength={255}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', fontSize: 12, boxSizing: 'border-box' }}
              />
            </div>
          )}
          {cart.length > 0 && (
            <div className="cart-footer">
              {addToOrderId && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F3E9FF', border: '1px solid #D9BFFF', borderRadius: 'var(--radius-sm)', padding: '6px 10px', marginBottom: 8, fontSize: 12, fontWeight: 500, color: '#6C3483' }}>
                  <span>➕ Dopisujesz do zamówienia #{addToOrderId}</span>
                  <button onClick={() => setAddToOrderId(null)}
                    style={{ background: 'none', border: 'none', color: '#6C3483', fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>
                    Anuluj
                  </button>
                </div>
              )}
              <div className="cart-total">Suma: <strong>{total.toFixed(2)} zł</strong></div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(addToOrderId || !contractor) && (
                  <button className="btn-primary" onClick={sendOrder} disabled={sending} style={{ flex: 2 }}>
                    {sending ? 'Wysyłanie...' : (addToOrderId ? '✓ Dopisz do zamówienia' : '✓ Wyślij do kuchni')}
                  </button>
                )}
                {!addToOrderId && contractor && (
                  <button onClick={sendToAccount} disabled={sending}
                    style={{ flex: 1, padding: '11px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                    🏢 Na konto
                  </button>
                )}
                {!addToOrderId && (
                  <button onClick={() => setPayTypeModal({ id: 'new', total, order: null })} disabled={sending}
                    style={{ flex: 1, padding: '11px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                    💳 Zapłać
                  </button>
                )}
              </div>
              {!addToOrderId && contractor && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Wybrany kontrahent — wyślij "Na konto" albo od razu "Zapłać", żeby zniżka się naliczyła.
                </div>
              )}
            </div>
          )}

          {openOrders.length > 0 && (
            <div className="open-orders">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="open-orders-title">Otwarte zamówienia</div>
                <button onClick={openDrawer} title="Otwórz szufladę"
                  style={{ padding: '4px 10px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', fontSize: 12, cursor: 'pointer', color: 'var(--text-muted)' }}>
                  🔔 Szuflada
                </button>
              </div>
              {openOrders.map(order => (
                <div key={order.id} className="open-order">
                  {order.kitchen_status === 'ready' && order.delivery_status === 'pending' && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#27AE60', marginBottom: 6 }}>✓ Gotowe do odbioru</div>
                  )}
                  {order.delivery_status === 'delivered' && order.payment_status === 'unpaid' && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6C3483', marginBottom: 6 }}>📦 Wydano — oczekuje na płatność</div>
                  )}
                  {order.payment_status === 'paid' && order.delivery_status === 'pending' && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#185FA5', marginBottom: 6 }}>💳 Opłacone — czeka na wydanie</div>
                  )}
                  {order.contractor_id && order.payment_status === 'unpaid' && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', marginBottom: 6 }}>🏢 Na konto kontrahenta — czeka na wydanie</div>
                  )}
                  <div className="oo-items">
                    {order.items.map((item, i) => (
                      <div key={i}>
                        <div className="oo-item">
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {item.item_kitchen_status === 'ready' && (
                              <span style={{ color: '#27AE60', fontWeight: 700, fontSize: 14 }}>✓</span>
                            )}
                            <span style={{ textDecoration: item.item_delivery_status === 'delivered' ? 'line-through' : 'none', color: item.item_delivery_status === 'delivered' ? 'var(--text-muted)' : 'inherit' }}>
                              {item.name} ×{item.qty}
                            </span>
                            {item.variant_label && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>({item.variant_label})</span>}
                          </span>
                          <span style={{ color: item.item_delivery_status === 'delivered' ? 'var(--text-muted)' : 'inherit' }}>{Number(item.subtotal).toFixed(2)} zł</span>
                        </div>
                        {(item.modifiers || []).map((mod, j) => (
                          <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', paddingLeft: 12, paddingBottom: 2 }}>
                            <span>+ {mod.name}</span>
                            <span>+{Number(mod.price).toFixed(2)} zł</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="oo-footer" style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '0.5px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                    <span className="oo-total" style={{ fontWeight: 600 }}>Razem: {Number(order.total).toFixed(2)} zł</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {order.kitchen_status === 'ready' && order.delivery_status === 'pending' && (
                        <button className="btn-pay" style={{ background: '#EAF3DE', color: 'var(--green)', borderColor: '#C0DD97' }}
                          onClick={() => markSent(order.id)}>Wydano</button>
                      )}
                      {order.payment_status === 'unpaid' && (
                        <button className="btn-pay" style={{ background: '#F3E9FF', color: '#6C3483', borderColor: '#D9BFFF' }}
                          onClick={() => { setAddToOrderId(order.id); showToast('Dodaj produkty do koszyka i wyślij do kuchni'); }}>
                          + Dodaj produkty
                        </button>
                      )}
                      {order.payment_status === 'unpaid' && (
                        <button className="btn-pay" style={{ background: '#FFF4E0', color: '#8A5A00', borderColor: '#F0D8A8' }}
                          onClick={() => printPreBill(order.id)}>
                          🧾 Rachunek wstępny
                        </button>
                      )}
                      {order.payment_status === 'unpaid' && !order.contractor_id && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-pay" style={{ background: 'var(--green)', color: '#fff', borderColor: 'var(--green)' }}
                            onClick={() => setPayTypeModal({ id: order.id, total: order.total, order })}>💳 Zapłać</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {payTypeModal && (
        <PayTypeModal
          total={payTypeModal.total}
          onClose={() => setPayTypeModal(null)}
          onParagon={() => {
            setPayTypeModal(null);
            setPayModal(payTypeModal.id);
          }}
          onParagonNip={() => {
            const snap = payTypeModal;
            setPayTypeModal(null);
            setNipReceiptModal({ id: snap.id, total: snap.total, isNew: snap.id === 'new' });
          }}
          onFaktura={() => {
            setPayTypeModal(null);
            const order = payTypeModal.order || { id: payTypeModal.id, total: payTypeModal.total, payment_status: 'unpaid', created_at: new Date().toISOString() };
            setInvoiceModal(order);
          }}
        />
      )}
      {nipReceiptModal && (
        <NipReceiptModal
          total={nipReceiptModal.total}
          onClose={() => setNipReceiptModal(null)}
          onPay={async (method, nip) => {
            setNipReceiptModal(null);
            setSending(true);
            try {
              let orderId = nipReceiptModal.id;
              if (nipReceiptModal.isNew) {
                const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
                const res = await fetch('/api/orders', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    table_id: parseInt(tableId),
                    items: cart.map(c => ({
                      menu_item_id:      c.menu_item_id,
                      qty:               c.qty,
                      variant_option_id: c.variant_option_id || undefined,
                      modifier_ids:      c.modifier_ids || [],
                      notes:             c.notes || undefined,
                    })),
                    payment_method: method,
                    contractor_id: contractor?.id || undefined,
                    notes: orderNote.trim() || undefined,
                  }),
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result.error || 'Błąd wysyłania zamówienia');
                setCart([]);
                setOrderNote('');
                orderId = result?.order_id;
                if (await shouldOpenDrawer(method)) await openDrawer();
              } else {
                await payOrder(nipReceiptModal.id, method);
                if (await shouldOpenDrawer(method)) await openDrawer();
              }
              if (await shouldPrintFiscal()) await printFiscal(orderId, method, nip);
              showToast('✓ Paragon z NIPem wydrukowany');
              navigate('/');
            } catch (err) { showToast(`Błąd: ${err.message}`); }
            finally { setSending(false); }
          }}
        />
      )}
      {invoiceModal && (
        <InvoiceFromOrderModal
          order={invoiceModal}
          onClose={() => setInvoiceModal(null)}
          onCreated={async (method) => {
            setInvoiceModal(null);
            showToast('✓ Faktura wystawiona');
            navigate('/');
          }}
          payOrder={async (orderId, method) => {
            if (orderId === 'new') {
              // Utwórz nowe zamówienie i opłać je (bez fiskalizacji — jest faktura)
              const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
              const res = await fetch('/api/orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  table_id: parseInt(tableId),
                  items: cart.map(c => ({
                    menu_item_id:      c.menu_item_id,
                    qty:               c.qty,
                    variant_option_id: c.variant_option_id || undefined,
                    modifier_ids:      c.modifier_ids || [],
                    notes:             c.notes || undefined,
                  })),
                  payment_method: method,
                  notes: orderNote.trim() || undefined,
                }),
              });
              const result = await res.json();
              if (!res.ok) throw new Error(result.error || 'Błąd wysyłania zamówienia');
              setCart([]);
              setOrderNote('');
              if (await shouldOpenDrawer(method)) await openDrawer();
              return result?.order_id;
            } else {
              await payOrder(orderId, method, true); // skipFiscal — drukujemy fakturę, nie paragon
              return orderId;
            }
          }}
          getOrderId={async (method) => {
            if (invoiceModal.id === 'new') {
              const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
              const res = await fetch('/api/orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  table_id: parseInt(tableId),
                  items: cart.map(c => ({
                    menu_item_id:      c.menu_item_id,
                    qty:               c.qty,
                    variant_option_id: c.variant_option_id || undefined,
                    modifier_ids:      c.modifier_ids || [],
                    notes:             c.notes || undefined,
                  })),
                  payment_method: method,
                  notes: orderNote.trim() || undefined,
                }),
              });
              const result = await res.json();
              if (!res.ok) throw new Error(result.error || 'Błąd wysyłania zamówienia');
              setCart([]);
              setOrderNote('');
              if (await shouldOpenDrawer(method)) await openDrawer();
              return result?.order_id;
            }
            return invoiceModal.id;
          }}
        />
      )}
      {payModal && (
        <PaymentModal
          onPay={(method) => {
            if (payModal === 'new') sendAndPay(method);
            else payOrder(payModal, method);
            setPayModal(null);
          }}
          onClose={() => setPayModal(null)}
          total={payModal === 'new' ? cart.reduce((s,c) => s+c.price*c.qty, 0) : openOrders.find(o=>o.id===payModal)?.total}
        />
      )}
      {itemModal && (
        <ItemModal item={itemModal} onAdd={addToCart} onClose={() => setItemModal(null)} getDiscountedPrice={getDiscountedPrice} />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function ItemModal({ item, onAdd, onClose, getDiscountedPrice }) {
  const [selectedVariant, setSelectedVariant] = useState(() => {
    const initial = {};
    (item.variant_groups || []).forEach(g => {
      const def = g.options.find(o => o.is_default);
      if (def) initial[g.id] = def.id;
    });
    return initial;
  });
  const [selectedMods, setSelectedMods]       = useState(() => {
    const defaults = [];
    (item.modifier_groups || []).forEach(g => {
      const def = g.items.find(i => i.is_default);
      if (def) defaults.push(def);
    });
    return defaults;
  });
  const [itemNote, setItemNote] = useState('');

  const allGroups     = item.variant_groups || [];
  const modifierGroups = item.modifier_groups || [];
  const maxVariantOptions = allGroups.reduce((max, g) => Math.max(max, (g.options || []).length), 0);
  const modalWidth    = Math.min(960, Math.max(380, modifierGroups.length * 250, maxVariantOptions * 160));
  const requiredGroups = allGroups.filter(g => g.required);
  const requiredModGroups = (item.modifier_groups || []).filter(g => g.required);
  const allModsSelected = requiredModGroups.every(g => g.items.some(i => selectedMods.find(m => m.id === i.id)));
  const allSelected   = requiredGroups.every(g => selectedVariant[g.id]) && allModsSelected;

  function toggleMod(mod, group) {
    setSelectedMods(prev => {
      const isMultiSelect = group ? !!group.multi_select : true;
      if (!isMultiSelect) {
        const groupItemIds = new Set((group.items || []).map(i => i.id));
        const alreadySelected = prev.some(m => m.id === mod.id);
        const withoutGroup = prev.filter(m => !groupItemIds.has(m.id));
        // W grupie wymaganej nie pozwalamy zejść do zera wyboru - kliknięcie
        // zaznaczonej opcji nic nie robi. W opcjonalnej - można odznaczyć.
        if (alreadySelected) return group.required ? prev : withoutGroup;
        return [...withoutGroup, mod];
      }
      return prev.find(m => m.id === mod.id)
        ? prev.filter(m => m.id !== mod.id)
        : [...prev, mod];
    });
  }

  function confirm() {
    const firstGroupId = allGroups[0]?.id;
    const variantOptId = firstGroupId ? selectedVariant[firstGroupId] : null;
    const labels = allGroups
      .filter(g => selectedVariant[g.id])
      .map(g => {
        const opt = g.options.find(o => o.id === selectedVariant[g.id]);
        return `${g.name}: ${opt.name}`;
      });
    onAdd(item, variantOptId, labels.join(', ') || null, selectedMods, itemNote.trim() || null);
  }

  const firstGroup = allGroups[0];
  const selectedOptId = firstGroup ? selectedVariant[firstGroup.id] : null;
  const baseVariantPrice = (() => {
    if (!firstGroup) return Number(item.price);
    if (!selectedOptId) return Number(item.price);
    return Number(firstGroup.options.find(o => o.id === selectedOptId)?.price || item.price);
  })();
  const discountedVariantPrice = selectedOptId
    ? (getDiscountedPrice(item, selectedOptId) ?? baseVariantPrice)
    : (getDiscountedPrice(item, null) ?? baseVariantPrice);
  const modPrice = selectedMods.reduce((s, m) => s + (getDiscountedPrice({ id: m.id, price: m.price }, null) ?? Number(m.price)), 0);
  const totalDisplay = discountedVariantPrice + modPrice;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: modalWidth, maxWidth: '92vw', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>{item.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        {allGroups.map(group => (
          <div key={group.id} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
              {group.name}
              {group.required && <span style={{ fontSize: 11, color: 'var(--red)' }}>wymagane</span>}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {group.options.map(opt => {
                const discounted = getDiscountedPrice(item, opt.id);
                return (
                  <button key={opt.id}
                    onClick={() => setSelectedVariant(s => ({ ...s, [group.id]: opt.id }))}
                    style={{
                      flex: '1 1 140px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 16px',
                      border: `${selectedVariant[group.id] === opt.id ? '2px solid var(--blue)' : '0.5px solid var(--border)'}`,
                      borderRadius: 8,
                      background: selectedVariant[group.id] === opt.id ? 'var(--blue-light)' : 'none',
                      cursor: 'pointer', fontSize: 14,
                    }}>
                    <span style={{ fontWeight: selectedVariant[group.id] === opt.id ? 500 : 400 }}>{opt.name}</span>
                    <span style={{ fontWeight: 500, color: selectedVariant[group.id] === opt.id ? 'var(--blue-dark)' : 'var(--text-muted)' }}>
                      {discounted !== null ? (
                        <>
                          <span style={{ textDecoration: 'line-through', fontSize: 11, marginRight: 4, opacity: 0.6 }}>{Number(opt.price).toFixed(2)}</span>
                          <span style={{ color: '#16a34a' }}>{discounted.toFixed(2)} zł</span>
                        </>
                      ) : `${Number(opt.price).toFixed(2)} zł`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {allGroups.length > 0 && modifierGroups.length > 0 && (
          <div style={{ borderTop: '0.5px solid var(--border)', marginBottom: 20 }} />
        )}

        <div style={{ columnWidth: 240, columnGap: 24, marginBottom: 20 }}>

        {modifierGroups.map(group => (
          <div key={group.id} style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
              {group.name}
              {group.required
                ? <span style={{ fontSize: 11, color: 'var(--red)' }}>wymagane</span>
                : <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(opcjonalne)</span>
              }
              {!group.multi_select && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>· wybierz jedno</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {group.items.map(mod => {
                const selected = selectedMods.find(m => m.id === mod.id);
                const modDiscounted = getDiscountedPrice({ id: mod.id, price: mod.price }, null);
                const modDisplayPrice = modDiscounted ?? Number(mod.price);
                return (
                  <button key={mod.id}
                    onClick={() => toggleMod(mod, group)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px',
                      border: `${selected ? '2px solid var(--green)' : '0.5px solid var(--border)'}`,
                      borderRadius: 8,
                      background: selected ? 'var(--green-light)' : 'none',
                      cursor: 'pointer', fontSize: 14,
                    }}>
                    <span style={{ fontWeight: selected ? 500 : 400 }}>{mod.name}</span>
                    <span style={{ fontWeight: 500, color: selected ? 'var(--green)' : 'var(--text-muted)' }}>
                      {modDiscounted !== null && modDiscounted !== Number(mod.price) ? (
                        <>
                          <span style={{ textDecoration: 'line-through', fontSize: 11, marginRight: 4, opacity: 0.6 }}>+{Number(mod.price).toFixed(2)}</span>
                          <span style={{ color: '#16a34a' }}>+{modDisplayPrice.toFixed(2)} zł</span>
                        </>
                      ) : (
                        Number(mod.price) > 0 ? `+${Number(mod.price).toFixed(2)} zł` : 'bezpłatny'
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        </div>

        <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 500 }}>
            <span>Łącznie</span>
            <span>{totalDisplay.toFixed(2)} zł</span>
          </div>
          {modPrice > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
              {discountedVariantPrice.toFixed(2)} zł + {modPrice.toFixed(2)} zł modyfikatory
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            value={itemNote}
            onChange={e => setItemNote(e.target.value)}
            placeholder="Komentarz do pozycji (opcjonalnie, np. bez pianki)"
            maxLength={255}
            style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>

        <button className="btn-primary" onClick={confirm} disabled={!allSelected}>
          Dodaj do zamówienia
        </button>
      </div>
    </div>
  );
}


const RECEIPT_NIP_LIMIT = 450;
function localDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function localDateFromValue(value) {
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function dueIn7Days(from) {
  const d = new Date(from);
  d.setDate(d.getDate() + 7);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function InvoiceFromOrderModal({ order, onClose, onCreated, payOrder, getOrderId }) {
  const total     = Number(order.total);
  const isPaid    = order.payment_status === 'paid';
  const isNew     = order.id === 'new';

  const [step, setStep]       = React.useState('form');
  const type = 'invoice';
  const [form, setForm]       = React.useState({ buyer_name: '', buyer_nip: '', buyer_address: '', buyer_city: '', buyer_zip: '', notes: '', buyer_is_jst: false, buyer_is_vat_group: false });
  const [hasRecipient, setHasRecipient] = React.useState(false);
  const [recipient, setRecipient]       = React.useState({ name: '', nip: '', address: '', city: '', zip: '' });
  const [dates, setDates]     = React.useState({
    issue_date:     localDateStr(),
    sale_date:      order.created_at ? localDateFromValue(order.created_at) : localDateStr(),
    payment_due:    dueIn7Days(localDateStr()),
    payment_method: (isPaid && order.payment_method) ? order.payment_method : 'transfer',
  });
  const [saving, setSaving]         = React.useState(false);
  const [error,  setError]          = React.useState('');
  const [done,   setDone]           = React.useState(null);
  const [gusLoading, setGusLoading] = React.useState(false);
  const [recGusLoading, setRecGusLoading] = React.useState(false);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setRec(k, v)   { setRecipient(r => ({ ...r, [k]: v })); }

  async function lookupGus() {
    const nip = form.buyer_nip.replace(/[\s-]/g, '');
    if (nip.length !== 10) { setError('Wpisz poprawny NIP (10 cyfr)'); return; }
    setGusLoading(true); setError('');
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res   = await fetch(`/api/invoices/gus/${nip}`, { headers: { Authorization: `Bearer ${token}` } });
      const data  = await res.json();
      if (!res.ok) throw new Error(data.error);
      setForm(f => ({ ...f, buyer_name: data.name, buyer_address: data.address, buyer_zip: data.zip, buyer_city: data.city }));
    } catch (err) { setError(`GUS: ${err.message}`); }
    finally { setGusLoading(false); }
  }

  async function lookupGusRecipient() {
    const nip = recipient.nip.replace(/[\s-]/g, '');
    if (nip.length !== 10) return;
    setRecGusLoading(true);
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res   = await fetch(`/api/invoices/gus/${nip}`, { headers: { Authorization: `Bearer ${token}` } });
      const data  = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRecipient(r => ({ ...r, name: data.name, address: data.address, zip: data.zip, city: data.city }));
    } catch (err) { setError(`GUS (odbiorca): ${err.message}`); }
    finally { setRecGusLoading(false); }
  }

  const needsAddress = type === 'invoice';
  const formValid = form.buyer_name && form.buyer_nip &&
    (!needsAddress || (form.buyer_address && form.buyer_city && form.buyer_zip));

  async function handlePayAndInvoice(method) {
    setSaving(true); setError('');
    try {
      const updatedDates = { ...dates, payment_method: method };

      // Pobierz order_id (tworząc zamówienie jeśli nowe)
      const orderId = isNew ? await getOrderId(method) : order.id;
      if (!orderId) throw new Error('Nie udało się utworzyć zamówienia');

      // Opłać jeśli nieopłacone i nie-nowe (nowe jest opłacane w getOrderId)
      if (!isPaid && !isNew) await payOrder(orderId, method, true); // skipFiscal — faktura zastępuje paragon

      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const body  = {
        type, order_id: orderId, ...form,
        ...updatedDates,
        ...(hasRecipient && recipient.name ? {
          recipient_name: recipient.name, recipient_nip: recipient.nip,
          recipient_address: recipient.address, recipient_city: recipient.city, recipient_zip: recipient.zip,
        } : {}),
      };
      const res  = await fetch('/api/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Błąd serwera');

      // Automatyczny wydruk potwierdzenia transakcji (jeśli włączone w Ustawieniach)
      // - nie blokuje sukcesu utworzenia faktury, gdyby druk się nie powiódł
      try {
        const settingsRes = await fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } });
        const settings = await settingsRes.json();
        if (settings.ksef_confirmation_auto_print === '1') {
          await fetch(`/api/invoices/${data.id}/print-confirmation`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      } catch {} // Błąd druku nie przerywa procesu wystawienia faktury

      setDone(data);
      onCreated(method);
    } catch (err) { setError(err.message); setSaving(false); }
  }

  function handleFormNext() {
    if (isPaid) handlePayAndInvoice(dates.payment_method);
    else setStep('payment');
  }

  const gross = total;

  // Ekran sukcesu
  if (done) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 360, maxWidth: '92vw', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Faktura wystawiona</div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>{done.invoice_number}</div>
        <button className="btn-primary" onClick={onClose}>Zamknij</button>
      </div>
    </div>
  );

  // Ekran wyboru płatności
  if (step === 'payment') {
    const methods = [
      { key: 'cash',     label: 'Gotówka', icon: '💵' },
      { key: 'card',     label: 'Karta',   icon: '💳' },
      { key: 'blik',     label: 'BLIK',    icon: '📱' },
      { key: 'transfer', label: 'Przelew', icon: '🏦' },
    ];
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 360, maxWidth: '92vw' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Forma płatności</h3>
            <button onClick={() => setStep('form')} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>←</button>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, textAlign: 'center', fontSize: 15 }}>
            Do zapłaty: <strong>{total.toFixed(2)} zł</strong>
          </div>
          {error && <div style={{ background: '#FCEBEB', color: 'var(--red)', fontSize: 13, padding: '10px 12px', borderRadius: 6, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {methods.map(m => (
              <button key={m.key} onClick={() => handlePayAndInvoice(m.key)} disabled={saving}
                style={{ padding: '16px 8px', borderRadius: 10, border: '0.5px solid var(--border)', background: 'var(--bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{m.icon}</div>
                {m.label}
              </button>
            ))}
          </div>
          {saving && <div style={{ textAlign: 'center', marginTop: 14, color: 'var(--text-muted)', fontSize: 13 }}>Przetwarzanie...</div>}
        </div>
      </div>
    );
  }

  // Ekran formularza
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 480, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>📄 Faktura do zamówienia #{order.id}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          Kwota zamówienia: <strong>{total.toFixed(2)} zł</strong>
        </div>

        {error && <div style={{ background: '#FCEBEB', color: 'var(--red)', fontSize: 13, padding: '10px 12px', borderRadius: 6, marginBottom: 14 }}>{error}</div>}

        <div className="field"><label>Nazwa nabywcy *</label>
          <input value={form.buyer_name} onChange={e => setField('buyer_name', e.target.value)} placeholder="Firma Sp. z o.o." />
        </div>
        <div className="field"><label>NIP *</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={form.buyer_nip} onChange={e => setField('buyer_nip', e.target.value)} placeholder="0000000000" maxLength={13}
              style={{ flex: 1, padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', fontSize: 14 }}
              onKeyDown={e => e.key === 'Enter' && lookupGus()} />
            <button type="button" onClick={lookupGus} disabled={gusLoading}
              style={{ padding: '0 14px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
              {gusLoading ? '⏳' : '🔍 GUS'}
            </button>
          </div>
        </div>

        {needsAddress && (
          <>
            <div className="field"><label>Adres *</label>
              <input value={form.buyer_address} onChange={e => setField('buyer_address', e.target.value)} placeholder="ul. Przykładowa 1" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
              <div className="field"><label>Kod pocztowy *</label><input value={form.buyer_zip} onChange={e => setField('buyer_zip', e.target.value)} placeholder="00-000" /></div>
              <div className="field"><label>Miasto *</label><input value={form.buyer_city} onChange={e => setField('buyer_city', e.target.value)} placeholder="Warszawa" /></div>
            </div>
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0 8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={form.buyer_is_jst} onChange={e => setField('buyer_is_jst', e.target.checked)} />
            Nabywca jest jednostką samorządu terytorialnego (JST)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={form.buyer_is_vat_group} onChange={e => setField('buyer_is_vat_group', e.target.checked)} />
            Nabywca należy do grupy VAT
          </label>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 0 8px', borderTop: '0.5px solid var(--border)', marginTop: 4 }}>
          Stawki VAT są pobierane z pozycji zamówienia.
        </div>

        {/* Odbiorca */}
        <div style={{ borderTop: '0.5px solid var(--border)', margin: '8px 0', paddingTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <input type="checkbox" checked={hasRecipient} onChange={e => setHasRecipient(e.target.checked)} />
            Dodaj odbiorcę (inny niż nabywca)
          </label>
        </div>
        {hasRecipient && (
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase' }}>Dane odbiorcy</div>
            <div className="field" style={{ margin: '0 0 8px' }}><label>NIP odbiorcy</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={recipient.nip} onChange={e => setRec('nip', e.target.value)} placeholder="0000000000" maxLength={13}
                  style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && lookupGusRecipient()} />
                <button type="button" onClick={lookupGusRecipient} disabled={recGusLoading}
                  style={{ padding: '0 14px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--surface)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                  {recGusLoading ? '⏳' : '🔍 GUS'}
                </button>
              </div>
            </div>
            <div className="field" style={{ margin: '0 0 8px' }}><label>Nazwa odbiorcy</label><input value={recipient.name} onChange={e => setRec('name', e.target.value)} placeholder="Firma Sp. z o.o." /></div>
            <div className="field" style={{ margin: '0 0 8px' }}><label>Adres</label><input value={recipient.address} onChange={e => setRec('address', e.target.value)} placeholder="ul. Przykładowa 1" /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
              <div className="field" style={{ margin: 0 }}><label>Kod pocztowy</label><input value={recipient.zip} onChange={e => setRec('zip', e.target.value)} placeholder="00-000" /></div>
              <div className="field" style={{ margin: 0 }}><label>Miasto</label><input value={recipient.city} onChange={e => setRec('city', e.target.value)} placeholder="Warszawa" /></div>
            </div>
          </div>
        )}

        {/* Daty */}
        <div style={{ borderTop: '0.5px solid var(--border)', margin: '8px 0', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase' }}>Daty</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <div className="field" style={{ margin: '0 0 8px' }}><label>Data wystawienia</label>
              <input type="date" value={dates.issue_date}
                onChange={e => setDates(d => ({ ...d, issue_date: e.target.value, payment_due: dueIn7Days(e.target.value) }))} />
            </div>
            <div className="field" style={{ margin: '0 0 8px' }}><label>Data sprzedaży</label>
              <input type="date" value={dates.sale_date} onChange={e => setDates(d => ({ ...d, sale_date: e.target.value }))} />
            </div>
            <div className="field" style={{ margin: 0 }}><label>Termin zapłaty</label>
              <input type="date" value={dates.payment_due} onChange={e => setDates(d => ({ ...d, payment_due: e.target.value }))} />
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', margin: '12px 0', fontSize: 13 }}>
          Brutto: <strong>{gross.toFixed(2)} zł</strong> · VAT wg stawek produktów
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Anuluj</button>
          <button className="btn-primary" onClick={handleFormNext} disabled={saving || !formValid} style={{ flex: 2 }}>
            {isPaid ? 'Wystaw fakturę' : 'Dalej — wybierz płatność →'}
          </button>
        </div>
      </div>
    </div>
  );
}


function PayTypeModal({ total, onClose, onParagon, onParagonNip, onFaktura }) {
  const totalNum = Number(total) || 0;
  const canReceiptNip = totalNum <= 450;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 340, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>Typ dokumentu</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 20, color: 'var(--text-primary)' }}>
          Do zapłaty: {totalNum.toFixed(2)} zł
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={onParagon} style={{
            padding: '14px 16px', border: '0.5px solid var(--border)', borderRadius: 10,
            background: 'var(--bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
          }}>
            <span style={{ fontSize: 26 }}>🧾</span>
            <span>Paragon</span>
          </button>
          {canReceiptNip ? (
            <button onClick={onParagonNip} style={{
              padding: '14px 16px', border: '0.5px solid var(--border)', borderRadius: 10,
              background: 'var(--bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
            }}>
              <span style={{ fontSize: 26 }}>🧾</span>
              <span>Paragon z NIPem nabywcy</span>
            </button>
          ) : (
            <div style={{
              padding: '14px 16px', border: '0.5px solid var(--border)', borderRadius: 10,
              background: 'var(--bg)', fontSize: 14, color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', gap: 12, opacity: 0.5,
            }}>
              <span style={{ fontSize: 26 }}>🧾</span>
              <span>Paragon z NIPem <span style={{ fontSize: 12 }}>(tylko do 450 zł)</span></span>
            </div>
          )}
          <button onClick={onFaktura} style={{
            padding: '14px 16px', border: '0.5px solid var(--blue)', borderRadius: 10,
            background: 'var(--blue-light)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            color: 'var(--blue-dark)', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
          }}>
            <span style={{ fontSize: 26 }}>📄</span>
            <span>Faktura VAT</span>
          </button>
        </div>
      </div>
    </div>
  );
}


function NipReceiptModal({ total, onClose, onPay }) {
  const [nip, setNip] = React.useState('');
  const [buyerName, setBuyerName] = React.useState('');
  const [gusLoading, setGusLoading] = React.useState(false);
  const [gusError, setGusError] = React.useState('');
  const [step, setStep] = React.useState('nip');
  const [saving, setSaving] = React.useState(false);
  const totalNum = Number(total) || 0;

  const nipClean = nip.replace(/[\s-]/g, '');
  const nipValid = nipClean.length === 10 && /^\d{10}$/.test(nipClean);
  const canProceed = nipValid && buyerName;

  async function lookupGus() {
    if (!nipValid) { setGusError('Wpisz poprawny NIP (10 cyfr)'); return; }
    setGusLoading(true); setGusError('');
    try {
      const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
      const res = await fetch(`/api/invoices/gus/${nipClean}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setBuyerName(data.name || '');
    } catch (err) {
      setGusError(`GUS: ${err.message}`);
    } finally { setGusLoading(false); }
  }

  async function handlePay(method) {
    setSaving(true);
    try {
      await onPay(method, nipClean);
    } catch (err) {
      setGusError(`Błąd: ${err.message}`);
      setSaving(false);
    }
  }

  const METHODS = [
    { key: 'cash', label: 'Gotówka', icon: '💵' },
    { key: 'card', label: 'Karta',   icon: '💳' },
    { key: 'blik', label: 'BLIK',    icon: '📱' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 350 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>🧾 Paragon z NIPem nabywcy</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '9px 14px', marginBottom: 16, fontSize: 14, fontWeight: 500 }}>
          Do zapłaty: <strong>{totalNum.toFixed(2)} zł</strong>
        </div>

        {step === 'nip' && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>NIP nabywcy</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  autoFocus
                  value={nip}
                  onChange={e => { setNip(e.target.value); setBuyerName(''); setGusError(''); }}
                  onKeyDown={e => e.key === 'Enter' && lookupGus()}
                  placeholder="0000000000"
                  maxLength={13}
                  style={{ flex: 1, padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', fontSize: 14 }}
                />
                <button onClick={lookupGus} disabled={gusLoading || !nipValid}
                  style={{ padding: '0 14px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', fontSize: 13, cursor: nipValid ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', color: 'var(--text-muted)', opacity: nipValid ? 1 : 0.5 }}>
                  {gusLoading ? '⏳' : '🔍 GUS'}
                </button>
              </div>
            </div>

            {buyerName && (
              <div style={{ background: '#f0fdf4', border: '0.5px solid #86efac', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
                <div style={{ color: '#15803d', fontWeight: 600, marginBottom: 2 }}>✓ Zweryfikowano w GUS</div>
                <div style={{ color: 'var(--text)' }}>{buyerName}</div>
              </div>
            )}

            {gusError && (
              <div style={{ background: '#FCEBEB', color: 'var(--red)', fontSize: 13, padding: '9px 12px', borderRadius: 6, marginBottom: 12 }}>{gusError}</div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={onClose}
                style={{ flex: 1, padding: '11px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>
                Anuluj
              </button>
              <button onClick={() => setStep('payment')} disabled={!canProceed}
                style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: canProceed ? 'var(--blue)' : 'var(--border)',
                  color: canProceed ? '#fff' : 'var(--text-muted)',
                  cursor: canProceed ? 'pointer' : 'not-allowed' }}>
                Dalej — wybierz płatność →
              </button>
            </div>
          </>
        )}

        {step === 'payment' && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              NIP: <strong style={{ color: 'var(--text)' }}>{nipClean}</strong>
              {buyerName && <span> · {buyerName}</span>}
            </div>
            {gusError && (
              <div style={{ background: '#FCEBEB', color: 'var(--red)', fontSize: 13, padding: '9px 12px', borderRadius: 6, marginBottom: 12 }}>{gusError}</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
              {METHODS.map(m => (
                <button key={m.key} onClick={() => handlePay(m.key)} disabled={saving}
                  style={{ padding: '16px 8px', border: '0.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}>
                  <span style={{ fontSize: 26 }}>{m.icon}</span>
                  {m.label}
                </button>
              ))}
            </div>
            <button onClick={() => setStep('nip')}
              style={{ width: '100%', padding: '10px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>
              ← Wróć do NIPu
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ContractorSelectModal({ onSelect, onClear, onClose, current }) {
  const [list, setList] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');

  React.useEffect(() => {
    fetch('/api/contractors', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setList).catch(() => {});
  }, []);

  const filtered = list.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Wybierz kontrahenta</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Szukaj..."
          style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', marginBottom: 10 }}
        />
        <div style={{ maxHeight: 300, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
          {filtered.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Brak kontrahentów</div>}
          {filtered.map((c, i) => (
            <div key={c.id} onClick={() => onSelect(c)}
              style={{ padding: '12px 14px', cursor: 'pointer', borderBottom: i < filtered.length-1 ? '0.5px solid var(--border)' : 'none', background: current?.id === c.id ? '#eff6ff' : 'transparent' }}>
              <div style={{ fontWeight: 500, fontSize: 14 }}>{c.name}</div>
              {c.nip && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>NIP: {c.nip}</div>}
            </div>
          ))}
        </div>
        {current && (
          <button onClick={onClear} style={{ width: '100%', padding: 10, border: '0.5px solid var(--border)', borderRadius: 8, background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>
            Usuń kontrahenta
          </button>
        )}
      </div>
    </div>
  );
}

function PaymentModal({ onPay, onClose, total }) {
  const [cashMode, setCashMode] = useState(false);
  const [received, setReceived] = useState('');

  const METHODS = [
    { key: 'cash',    label: 'Gotówka',  icon: '💵' },
    { key: 'card',    label: 'Karta',    icon: '💳' },
    { key: 'blik',    label: 'BLIK',     icon: '📱' },
    { key: 'receipt', label: 'Paragon',  icon: '🧾' },
  ];

  const totalNum = Number(total) || 0;
  const receivedNum = parseFloat(received.replace(',', '.')) || 0;
  const change = receivedNum - totalNum;
  const canPay = receivedNum >= totalNum;

  if (cashMode) {
    const handleKey = (key) => {
      if (key === 'C') { setReceived(''); return; }
      if (key === '⌫') { setReceived(prev => prev.slice(0, -1)); return; }
      if (key === '.' && received.includes('.')) return;
      if (received === '0' && key !== '.') { setReceived(key); return; }
      if (received.length >= 8) return;
      setReceived(prev => prev + key);
    };

    const KEYS = [
      ['7','8','9'],
      ['4','5','6'],
      ['1','2','3'],
      ['C','0','.'],
    ];

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 340, maxWidth: '90vw' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 500 }}>💵 Płatność gotówką</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
          </div>

          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 14, color: 'var(--text-primary)' }}>
            Do zapłaty: {totalNum.toFixed(2)} zł
          </div>

          {/* Wyświetlacz */}
          <div style={{ background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Otrzymano</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', minHeight: 36 }}>
              {received || '0'} <span style={{ fontSize: 16, fontWeight: 500 }}>zł</span>
            </div>
          </div>

          {/* Reszta */}
          <div style={{ background: canPay ? '#f0fdf4' : 'var(--bg)', border: `1px solid ${canPay ? '#bbf7d0' : 'var(--border)'}`, borderRadius: 8, padding: '8px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>Reszta</span>
            <span style={{ fontSize: 22, fontWeight: 700, color: canPay ? '#16a34a' : 'var(--text-muted)' }}>
              {canPay ? change.toFixed(2) + ' zł' : '—'}
            </span>
          </div>

          {/* Klawiatura */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
            {KEYS.flat().map(key => (
              <button key={key} onClick={() => handleKey(key)} style={{
                padding: '16px 0', fontSize: key === 'C' ? 14 : 20, fontWeight: 600,
                border: '0.5px solid var(--border)', borderRadius: 8, cursor: 'pointer',
                background: key === 'C' ? '#fef2f2' : 'var(--bg)',
                color: key === 'C' ? '#dc2626' : 'var(--text)',
              }}>
                {key}
              </button>
            ))}
            <button onClick={() => handleKey('⌫')} style={{
              padding: '16px 0', fontSize: 20, fontWeight: 600,
              border: '0.5px solid var(--border)', borderRadius: 8, cursor: 'pointer',
              background: 'var(--bg)', color: 'var(--text-muted)',
            }}>⌫</button>
          </div>

          {/* Skróty */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 14 }}>
            {[10, 20, 50, 100].map(amt => (
              <button key={amt} onClick={() => setReceived(String(amt))} style={{
                padding: '7px 4px', border: '0.5px solid var(--border)', borderRadius: 6,
                background: 'var(--bg)', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-muted)',
              }}>
                {amt} zł
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setCashMode(false)} style={{ flex: 1, padding: '13px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>
              ← Wróć
            </button>
            <button onClick={() => onPay('cash')} disabled={!canPay} style={{
              flex: 2, padding: '13px', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
              background: canPay ? 'var(--blue)' : 'var(--border)',
              color: canPay ? '#fff' : 'var(--text-muted)',
              cursor: canPay ? 'pointer' : 'not-allowed',
            }}>
              Zatwierdź
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 340, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>Metoda płatności</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>
        {total && (
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 20, color: 'var(--text-primary)' }}>
            Do zapłaty: {totalNum.toFixed(2)} zł
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {METHODS.map(m => (
            <button key={m.key} onClick={() => m.key === 'cash' ? setCashMode(true) : onPay(m.key)} style={{
              padding: '16px 12px', border: '0.5px solid var(--border)', borderRadius: 10,
              background: 'var(--bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontSize: 28 }}>{m.icon}</span>
              {m.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
